import {
  createContext,
  ReactNode,
  RefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'

import { PreviewPanelHandle } from '../../preview/PreviewPanel'

export interface PreviewControlValue {
  /** PreviewModal が内包する PreviewPanel への参照（HeaderMenu と共有する）。 */
  panelRef: RefObject<PreviewPanelHandle>
  /**
   * パネルの物理初期化が完了し操作可能か。PreviewModal が自身の ready を共有へ
   * 反映する。未 ready の間はヘッダーの再生/停止/リセットを無効化する。
   */
  ready: boolean
  /** PreviewModal が自身の ready 状態を共有へ反映するための setter。 */
  setReady: (ready: boolean) => void
  /**
   * おためしプレイが実行中か。真実源は PreviewPanel（onRunningChange で同期）。
   * 実行中は再生ボタンを、停止中は停止ボタンを無効化する（連打防止）。
   */
  running: boolean
  /** PreviewModal が PreviewPanel の onRunningChange を共有へ反映するための setter。 */
  setRunning: (running: boolean) => void
  /**
   * 音量(0..1)。真実源は PreviewPanel（onVolumeChange で同期）。
   * ヘッダー（PreviewRunControls）のスライダー位置・アイコン切替に使う。
   */
  volume: number
  /** PreviewModal が PreviewPanel の onVolumeChange を共有へ反映するための setter。 */
  setVolume: (volume: number) => void
  /** World・アバターを初期状態へ戻し、おためしプレイを最初から開始する。 */
  start: () => void
  /** おためしプレイをその場で停止し、キャラクター操作も止める（凍結）。 */
  stop: () => void
  /** 音量を変更する（PreviewPanel へ委譲する）。スライダー操作から呼ぶ。 */
  changeVolume: (volume: number) => void
  /** 音量 0 と直前音量をトグルする（PreviewPanel へ委譲する）。 */
  toggleMute: () => void
}

const noop = (): void => {}

/**
 * プレビュー（おためしプレイ）の再生制御を、ヘッダー（HeaderMenu）と
 * フローティングの PreviewModal の間で共有するためのコンテキスト。
 * Provider 未配置（list / error ページ等）では no-op を返すため、
 * どのページでも HeaderMenu を安全にマウントできる。
 */
export const PreviewControlContext = createContext<PreviewControlValue>({
  panelRef: { current: null },
  ready: false,
  setReady: noop,
  running: false,
  setRunning: noop,
  volume: 1,
  setVolume: noop,
  start: noop,
  stop: noop,
  changeVolume: noop,
  toggleMute: noop,
})

export const PreviewControlProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const panelRef = useRef<PreviewPanelHandle>(null)
  // パネル未マウント/初期化中は false。PreviewModal が ready を反映する。
  const [ready, setReadyState] = useState(false)
  // 実行中フラグ。真実源は PreviewPanel で、PreviewModal が onRunningChange を
  // setRunning へ配線して同期する（パネル内部での停止も漏れなく反映される）。
  const [running, setRunning] = useState(false)
  // 音量。真実源は PreviewPanel で、PreviewModal が onVolumeChange を setVolume へ
  // 配線して同期する。スライダーで変えてもアイコン・位置が整合する。
  const [volume, setVolume] = useState(1)

  // ready=false はパネルの破棄・差し替え（あたらしくつくる等の refreshKey 再
  // マウント、プレビュー非表示化）を意味し、実行も必ず止まっている。実行中
  // フラグを残すと新しいパネルで再生ボタンが無効のままになるため、ここで
  // まとめて初期化する。
  const setReady = useCallback((next: boolean): void => {
    setReadyState(next)
    if (!next) {
      setRunning(false)
      // 新パネルは音量 1（最大）で始まる（PreviewPanel がマウント時に
      // setAudioVolume で揃える）。共有状態も合わせて戻す。
      setVolume(1)
    }
  }, [])
  // 未 ready の panelRef（切替中で null/未初期化）へ命令が飛ぶのを防ぐため、
  // ボタン無効化に加えてここでも ready ガードする（多重防御）。
  // running の更新はパネルの onRunningChange に任せる（二重管理しない）。
  const start = useCallback((): void => {
    if (ready) {
      panelRef.current?.start()
    }
  }, [ready])
  const stop = useCallback((): void => {
    if (ready) {
      panelRef.current?.stop()
    }
  }, [ready])
  // 音量操作はパネルへ委譲する。volume の更新はパネルの onVolumeChange に任せる
  // （二重管理しない）。未 ready（panelRef 未初期化）では no-op になる。
  const changeVolume = useCallback(
    (next: number): void => {
      if (ready) {
        panelRef.current?.setVolume(next)
      }
    },
    [ready],
  )
  const toggleMute = useCallback((): void => {
    if (ready) {
      panelRef.current?.toggleMute()
    }
  }, [ready])
  const value = useMemo<PreviewControlValue>(
    () => ({
      panelRef,
      ready,
      setReady,
      running,
      setRunning,
      volume,
      setVolume,
      start,
      stop,
      changeVolume,
      toggleMute,
    }),
    [ready, setReady, running, volume, start, stop, changeVolume, toggleMute],
  )
  return (
    <PreviewControlContext.Provider value={value}>
      {children}
    </PreviewControlContext.Provider>
  )
}
